import React from "react";
import { Box, Typography } from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";

interface Props {
  label: string;
  hint?: string;
  selected: boolean;
  onClick: () => void;
}

/** A large, calm, selectable card. ≥44px target, soft surface, gold check when
 *  selected. Used for topic/subtopic/region/fund choices. */
export default function ChoiceCard({ label, hint, selected, onClick }: Props) {
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onClick()}
      sx={{
        display: "flex", alignItems: "center", gap: 2, cursor: "pointer", userSelect: "none",
        p: 2.25, borderRadius: "var(--radius-lg)",
        bgcolor: selected ? "var(--primary-50)" : "var(--surface)",
        border: "2px solid", borderColor: selected ? "primary.main" : "var(--border)",
        boxShadow: selected ? "none" : "var(--shadow-sm)",
        transition: "border-color .2s, background-color .2s, transform .1s",
        "&:hover": { borderColor: selected ? "primary.main" : "var(--primary-300)" },
        "&:active": { transform: "scale(0.995)" },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 600, color: "text.primary", fontSize: "1.05rem" }}>{label}</Typography>
        {hint && <Typography sx={{ color: "text.secondary", fontSize: ".9rem", mt: 0.25 }}>{hint}</Typography>}
      </Box>
      <Box
        sx={{
          flexShrink: 0, width: 26, height: 26, borderRadius: "50%",
          display: "grid", placeItems: "center",
          bgcolor: selected ? "primary.main" : "transparent",
          border: selected ? "none" : "2px solid var(--neutral-300)",
          color: "#fff", transition: "background-color .2s",
        }}
      >
        {selected && <CheckRoundedIcon sx={{ fontSize: 18 }} />}
      </Box>
    </Box>
  );
}
